import { Info, MagnifyingGlass } from '@phosphor-icons/react'
import { LinkExternalIcon } from '@primer/octicons-react'
import React, { useContext, useEffect, useMemo, useState } from 'react'

import SearchInput from '@/components/shared/components/SearchInput'
import Button from '@/components/shared/ui/Button'
import UnstyledLink from '@/components/shared/ui/Links/UnstyledLink'
import Tooltip from '@/components/shared/ui/Tooltip'
import { POST_SEARCH_ATTRIBUTES_TO_RETRIVE } from '@/config/module/postsConstants'
import HNContext from '@/context/HNContext'
import { useTranslations } from '@/hooks/useTranslations'
import {
  addPostToKanbanEntry,
  getKanbanTopPosts,
  removePostFromKanbanEntry,
} from '@/models/Kanban'
import type { IBoardDetails } from '@/types/board'
import type { IPost } from '@/types/post'
import type { IKanbanItem } from '@/types/roadmap'
import type { ISearchResponse } from '@/types/search'
import toaster from '@/utils/toast'

import KanbanTaggedPost from './KanbanTaggedPost'

interface IPropTypes {
  kanbanItem: IKanbanItem
  onAdd?: (post: IPost) => void
  onRemove?: (post: IPost) => void
  onUpdate?: (kanbanItem: IKanbanItem) => void
  readonly?: boolean
}
export default function KanbanNewEntryPostPicker({
  kanbanItem: _kanbanItem,
  onAdd,
  onRemove,
  onUpdate,
  readonly,
}: IPropTypes) {
  const t = useTranslations('roadmap.kanban.addNewEntry.tagPost')
  const {
    organization,
    userProfile,
    buckets = [],
    isAdmin,
  } = useContext(HNContext)
  const [searchedPosts, setSearchedPosts] = useState<IPost[]>([])
  const [query, setQuery] = useState<string | null>(null)
  const [topPosts, setTopPosts] = useState<IPost[]>([])
  const [kanbanItem, setKanbanItem] = useState(_kanbanItem)

  const boardIds = useMemo(
    () => buckets.map((b: IBoardDetails) => `bucket_id=${b.id}`),
    [buckets]
  )

  const fetchTopPosts = () => {
    return getKanbanTopPosts().then(setTopPosts).catch(toaster.error)
  }

  const tagPost = (post: IPost) => {
    if (kanbanItem.id) {
      addPostToKanbanEntry(kanbanItem.id, { feature_request_id: post.id }).then(
        (addedPost: IPost) => {
          if (onUpdate) {
            onUpdate({
              ...kanbanItem,
              posts: [...(kanbanItem.posts || []), addedPost],
            })
          }
        }
      )
    } else if (onAdd) {
      onAdd(post)
    }
  }

  const removePost = (post: IPost) => {
    if (kanbanItem.id) {
      removePostFromKanbanEntry(post.id, {
        kanban_entry_id: kanbanItem.id,
      }).then(() => {
        if (onUpdate) {
          onUpdate({
            ...kanbanItem,
            posts: (kanbanItem.posts || []).filter((p) => p.id !== post.id),
          })
        }
      })
    } else if (onRemove) {
      onRemove(post)
    }
  }

  useEffect(() => {
    if (userProfile && isAdmin) fetchTopPosts()
  }, [])

  useEffect(() => {
    setKanbanItem(_kanbanItem)
  }, [_kanbanItem])

  const handleSearch = (results: ISearchResponse[]) => {
    if (!results.length) return
    const searchResponse = results[0]
    setQuery(searchResponse?.query || null)
    setSearchedPosts((searchResponse?.hits as IPost[]) || [])
  }

  const renderSearchedPost = (post: IPost) => (
    <li key={post.id} className='group bg-snow transition '>
      <div className='mx-auto flex max-w-7xl items-center pl-3'>
        <div className='flex w-full items-center space-x-2 overflow-x-auto overflow-y-hidden py-1.5 pl-1 pr-4 text-gray12 transition focus:outline-none '>
          <span className='flex-1 truncate text-left text-sm'>
            {post.title}
          </span>
          <UnstyledLink
            target='_blank'
            href={`/p/${post.slug}`}
            className='shadow-none'
          >
            <LinkExternalIcon className='h-4 w-4' />
          </UnstyledLink>
          <Button
            disabled={!!(kanbanItem?.posts || []).find((p) => p.id === post.id)}
            size='xxs'
            variant='secondary'
            type='button'
            onClick={() => tagPost(post)}
          >
            {t('buttons.add', { ignorePrefix: true })}
          </Button>
        </div>
      </div>
    </li>
  )

  const renderTaggedPost = (post: IPost) => (
    <KanbanTaggedPost
      key={post.id}
      post={post}
      onRemove={removePost}
      readonly={readonly}
    />
  )

  const renderLatestPosts = () => (
    <ul className='divide-y divide-gray5 transition '>
      {topPosts.map(renderSearchedPost)}
    </ul>
  )

  const renderTaggedPosts = () => (
    <>
      <p className='mb-2 pl-3 text-gray8 '>{t('taggedPosts.title')}</p>
      {kanbanItem?.posts?.length ? (
        <ul className='mb-0 divide-y divide-gray5 border-y border-gray5 bg-snow py-1 text-center text-gray8 transition '>
          {kanbanItem?.posts?.length &&
            kanbanItem?.posts?.map(renderTaggedPost)}
        </ul>
      ) : (
        <p className='mb-0 border-y border-gray5 bg-snow py-2.5 text-center text-gray8 '>
          {t('taggedPosts.empty')}
        </p>
      )}
    </>
  )

  const renderPostSearch = () => (
    <>
      <div className='mb-2 flex items-center space-x-1 pl-3 text-gray8 '>
        <span>{t('searchPosts.title')}</span>
        <Tooltip text={t('searchPosts.hintText')} asChild>
          <Info weight='bold' className='h-4 w-4 text-gray8 ' />
        </Tooltip>
      </div>
      <div className='relative w-full text-gray7 focus-within:text-gray9'>
        <div className='pointer-events-none absolute inset-y-0 left-2 flex items-center'>
          <MagnifyingGlass weight='bold' className='h-5 w-5' />
        </div>
        <SearchInput
          indexes={[
            {
              label: '',
              indexName: 'FeatureRequest',
              attributesToRetrieve: POST_SEARCH_ATTRIBUTES_TO_RETRIVE,
              filters: [
                boardIds,
                `organization_id=${organization?.id}`,
                'merged_with_id=0',
                'hidden=false',
                'approval_status=approved',
              ],
            },
          ]}
          onSearch={handleSearch}
          inputProps={{
            placeholder: t('searchPosts.placeholder'),
            className:
              'block h-full w-full border-transparent bg-snow py-3 pl-10 pr-4 text-gray12 placeholder:text-gray8 focus:border-transparent focus:outline-none focus:ring-0 focus:placeholder:text-gray7 sm:text-sm',
          }}
        />
      </div>
      <ul className='mb-0 divide-y divide-gray5 border-t border-gray5  '>
        {searchedPosts.map(renderSearchedPost)}
      </ul>

      {!query?.length && topPosts.length ? renderLatestPosts() : null}
    </>
  )

  return (
    <aside className='min-h-full rounded border border-gray5 bg-gray3 py-4   lg:col-span-3'>
      <div className='pb-4'>{renderTaggedPosts()}</div>
      <div>{!readonly && renderPostSearch()}</div>
    </aside>
  )
}
